import { SliderTimeContext } from 'contexts/SliderTimeContext'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectSourcePlayerProgress } from 'selectors/preview'
import { timelineTimeToSeconds } from '~/Util'

/**
 * @typedef {object} ReactPlayerState
 * @property {boolean} isLoading
 * @property {boolean} isPlaying
 * @property {boolean} isReady
 */

const DEFAULT_STATE = {
  isReady: false,
  isPlaying: false,
  isLoading: false,
  isBuffering: false,
  progress: null,
  paused: null,
  duration: null,
  currentTime: null,
}

/**
 * @param player
 * @return {{isLoading: boolean, isPlaying: boolean, isReady: boolean}}
 */
export function getPlayerState(player) {
  // it can be both null and undefined, so non-strict comparison here
  const internalPlayer = player?.getInternalPlayer()
  if (!internalPlayer) {
    return DEFAULT_STATE
  }

  const { isReady, isPlaying, isLoading, buffering } = player
  const { paused, duration, currentTime } = internalPlayer
  const { isBuffering, progress } = buffering

  return { isReady,
    isPlaying,
    isLoading,
    isBuffering,
    progress,
    paused,
    duration,
    currentTime }
}

export default function usePlayer(params = {}) {
  const {
    isSourceMediaPlayer,
    shouldRewind,
    latestTimelineRewindTime,
    isBackground,
    mode,
    asset,
  } = params
  const { startTime, mediaStart } = asset ?? {}
  const { localTimeRef } = useContext(SliderTimeContext)
  // Store the player instance in Ref to avoid triggering unnecessary re-renders
  // and avoid dependency issues with useCallback
  const playerRef = useRef(null)
  const [ isReady, setIsReady ] = useState(false)

  const memoMediaProgress = useSelector(selectSourcePlayerProgress)

  // To synchronize the timeline and player time when re-editing and mounting the player
  const synchronizeTime = useCallback(timeNsUnit => {
    const seekTime = timelineTimeToSeconds(
      Math.max(timeNsUnit - startTime, 0) + mediaStart
    )
    if (seekTime !== playerRef.current.getCurrentTime()) {
      playerRef.current.seekTo(seekTime)
    }
  }, [ startTime, mediaStart ])

  const onReadyPlayer = useCallback(playerInstance => {
    playerRef.current = playerInstance
    if (isSourceMediaPlayer && playerInstance) {
      playerInstance.seekTo(memoMediaProgress)
    } else if (!isSourceMediaPlayer && !isBackground) {
      synchronizeTime(localTimeRef.current)
    }
    setIsReady(true)
  }, [ isSourceMediaPlayer, isBackground, memoMediaProgress, synchronizeTime, localTimeRef ])

  // A wrapper for callbacks that want to work with a player instance
  const usePlayerCallback = useCallback(fn => (...args) => {
    if (isReady && playerRef.current) {
      fn(playerRef.current, ...args)
    }
  }, [ isReady ])

  // Custom hook method, for the timeline rewind only (need a fix later)
  const usePlayerEffect = () => {
    useEffect(() => {
      if (mode === 'test') return

      if (isReady && playerRef.current) {
        if (shouldRewind && latestTimelineRewindTime !== null) {
          synchronizeTime(latestTimelineRewindTime)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isReady, shouldRewind, latestTimelineRewindTime, mode ])
  }

  return [
    onReadyPlayer,
    {
      getState: useCallback(() => getPlayerState(playerRef.current), []),
      seek: useCallback(time => {
        if (playerRef.current) {
          playerRef.current.seekTo(time)
        }
      }, [ playerRef ]),
      usePlayerCallback,
      usePlayerEffect,
    },
  ]
}
